<!-- 商品管理 V2 - SPU 绑定弹窗 -->
<template>
  <div>
    <el-dialog
      :title="$t('message.productV2SpuBind')"
      :visible.sync="visible"
      width="960px"
      append-to-body
      :close-on-click-modal="false"
      custom-class="bind-spu-dialog"
    >
      <div v-loading="loading" class="dialog-body">
        <!-- 当前已绑定 SPU：打开弹框时带入 product.spu（id/spu/name/image），内容区自由撑开 -->
        <div class="bound-row">
          <span class="bound-label">{{ $t('message.productV2CurrentBoundSpu') }}</span>
          <div class="bound-content">
            <template v-if="boundSpu && boundSpu.spu">
              <strong class="bound-code">{{ boundSpu.spu }}</strong>
              <el-image
                v-if="boundSpu.image"
                class="bound-pic"
                :src="boundSpu.image"
                fit="cover"
                :preview-src-list="[boundSpu.image]"
              />
              <span class="bound-name">{{ boundSpu.name || '' }}</span>
              <el-button class="bound-unbind" type="danger" size="small" @click="unbindSpu()">{{ $t('message.productV2Unbind') }}</el-button>
            </template>
          </div>
        </div>

        <!-- 搜索 + 创建：输入框与按钮统一高度 -->
        <div class="toolbar">
          <div class="search-area">
            <el-input
              v-model="keyword"
              clearable
              size="small"
              prefix-icon="el-icon-search"
              :placeholder="$t('message.productV2SpuSearchPlaceholder')"
              @keyup.enter.native="search"
            />
            <el-button type="primary" size="small" @click="search">{{ $t('message.search') }}</el-button>
          </div>
          <el-button class="create-btn" size="small" @click="openSaveDialog()">{{ $t('message.productV2CreateSpu') }}</el-button>
        </div>

        <!-- SPU 列表：表头固定，内容最高 600px，超出滚动 -->
        <div class="spu-table">
          <div class="spu-grid table-head">
            <div>SPU</div>
            <div>{{ $t('message.picture') }}</div>
            <div>{{ $t('message.productV2SpuName') }}</div>
            <div>{{ $t('message.operation') }}</div>
          </div>
          <div class="spu-table-body">
            <div v-for="(row, index) in list" :key="row.id || row.spu || index" class="spu-grid table-row">
              <div>{{ row.spu }}</div>
              <div>
                <el-image
                  v-if="row.image"
                  class="row-pic"
                  :src="row.image"
                  fit="cover"
                  :preview-src-list="[row.image]"
                />
                <span v-else>-</span>
              </div>
              <div class="row-name">{{ row.name || '-' }}</div>
              <div class="row-actions">
                <i class="el-icon-delete action-icon delete" @click="deleteSpu(row)"></i>
                <i class="el-icon-edit-outline action-icon edit" @click="openSaveDialog(row)"></i>
                <!-- 列表操作统一为绑定；已绑定项在顶部解绑。一个管理番号仅允许绑定一个 SPU -->
                <el-button
                  type="primary"
                  size="mini"
                  :loading="bindingId === row.id"
                  @click="bindSpu(row)"
                >{{ $t('message.productV2Bind') }}</el-button>
              </div>
            </div>
            <div v-if="!loading && !list.length" class="empty">{{ $t('message.productV2NoData') }}</div>
          </div>
        </div>

        <div v-if="total" class="pagination">
          <el-pagination
            :current-page="page"
            :page-sizes="[20, 50, 100, 200]"
            :page-size="pageSize"
            layout="total, sizes, prev, pager, next, jumper"
            :total="total"
            @size-change="changePageSize"
            @current-change="changePage"
          />
        </div>
      </div>
    </el-dialog>

    <!-- SPU 创建 / 编辑弹窗：spu 必填；名称、图片可空；图片最多 1 张，走 OSS -->
    <el-dialog
      :title="$t('message.productV2SpuSaveTitle')"
      :visible.sync="saveVisible"
      width="520px"
      append-to-body
      :close-on-click-modal="false"
      custom-class="spu-save-dialog"
    >
      <el-form class="save-form" label-width="80px" @submit.native.prevent>
        <el-form-item required :label="'SPU'">
          <el-input v-model="saveForm.spu" clearable :placeholder="$t('message.productV2EnterSpu')" />
        </el-form-item>
        <el-form-item :label="$t('message.productV2SpuName')">
          <el-input v-model="saveForm.name" clearable :placeholder="$t('message.productV2EnterSpuName')" />
        </el-form-item>
        <el-form-item :label="$t('message.picture')">
          <div class="image-row">
            <el-upload
              action="#"
              :show-file-list="false"
              :http-request="uploadSpuImage"
              accept="image/*"
            >
              <el-button class="upload-btn" size="small" :loading="uploading">{{ $t('message.productV2UploadImage') }}</el-button>
            </el-upload>
            <div v-if="saveForm.image" class="image-preview">
              <el-image :src="saveForm.image" fit="cover" :preview-src-list="[saveForm.image]" />
              <i class="el-icon-close" @click="saveForm.image = ''"></i>
            </div>
          </div>
        </el-form-item>
      </el-form>
      <div class="save-footer">
        <el-button class="save-btn" type="danger" :loading="saving" @click="submitSave">{{ $t('message.save') }}</el-button>
      </div>
    </el-dialog>

    <!-- 删除 SPU 二次确认：删除后自动解绑全部已绑定管理番号，不可回退 -->
    <el-dialog
      :title="$t('message.productV2DeletePrompt')"
      :visible.sync="deleteVisible"
      width="480px"
      append-to-body
      :close-on-click-modal="false"
      custom-class="spu-delete-dialog"
    >
      <p class="delete-warning">{{ $t('message.productV2SpuDeleteWarning') }}</p>
      <span slot="footer">
        <el-button @click="deleteVisible = false">{{ $t('message.cancel') }}</el-button>
        <el-button type="danger" :loading="deleting" @click="submitDelete">{{ $t('message.confirmDeletion') }}</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
/**
 * SPU 绑定弹窗
 * - 列表：/admin/product/v2/spuList
 * - 创建/编辑：/admin/product/v2/spuSave（spu 必填，名称/图片可空，图片 OSS 单图）
 * - 删除：/admin/product/v2/spuDelete（二次确认后删除，已绑定管理番号自动解绑）
 * - 绑定：/admin/product/v2/spuBind（刷新弹框列表；通过 product 引用 + change 同步列表行/详情）
 * - 一个管理番号仅绑定一个 SPU；解绑同步清空列表行/详情展示
 */
export default {
  name: 'BindSpuDialog',
  data() {
    return {
      visible: false,
      loading: false,
      product: {}, // 当前列表行商品
      boundSpu: null, // 当前已绑定的 SPU
      keyword: '',
      list: [],
      total: 0,
      page: 1,
      pageSize: 20,
      // 创建/编辑
      saveVisible: false,
      saving: false,
      uploading: false,
      saveForm: {
        id: '',
        spu: '',
        name: '',
        image: ''
      },
      // 删除确认
      deleteVisible: false,
      deleting: false,
      deleteRow: null,
      bindingId: null // 当前正在绑定的 SPU id
    }
  },
  methods: {
    // 打开绑定弹窗：带入商品行已绑定的 spu（id/spu/name/image），并拉取客户 SPU 列表
    open(product) {
      this.product = product || {}
      // 直接使用列表返回的 spu 对象，不强行改字段
      const spu = product && product.spu
      this.boundSpu = spu && typeof spu === 'object' && spu.spu
        ? { id: spu.id, spu: spu.spu, name: spu.name, image: spu.image }
        : null
      this.keyword = ''
      this.page = 1
      this.pageSize = 20
      this.list = []
      this.total = 0
      this.visible = true
      this.loadList()
    },
    getUserId() {
      return this.product.user_id || (this.product.user && this.product.user.id)
    },
    // 拉取 V2-SPU 列表
    loadList() {
      const user_id = this.getUserId()
      if (!user_id) {
        this.list = []
        this.total = 0
        return
      }
      this.loading = true
      this.$api.productManagementApi.spuList({
        user_id,
        keyword: this.keyword,
        page: this.page,
        pageSize: this.pageSize
      }).then(res => {
        const data = (res && res.data) || {}
        this.list = data.list || data.data || []
        this.total = data.total || 0
      }).finally(() => {
        this.loading = false
      })
    },
    search() {
      this.page = 1
      this.loadList()
    },
    changePage(page) {
      this.page = page
      this.loadList()
    },
    changePageSize(pageSize) {
      this.pageSize = pageSize
      this.page = 1
      this.loadList()
    },
    // 判断列表行是否为当前已绑定 SPU
    isBoundRow(row) {
      if (!this.boundSpu || !row) return false
      if (this.boundSpu.id != null && row.id != null && String(this.boundSpu.id) === String(row.id)) return true
      return this.boundSpu.spu && row.spu && String(this.boundSpu.spu) === String(row.spu)
    },
    /**
     * 绑定 SPU：调用 spuBind
     * 成功后只刷新弹框列表；通过 product 引用 + change 事件同步列表行/详情页，不刷新外层列表
     */
    bindSpu(row) {
      const user_id = this.getUserId()
      const sys_code = this.product.sys_code
      const spu_id = row && row.id
      if (!user_id || !sys_code || !spu_id) return
      this.bindingId = spu_id
      this.$api.productManagementApi.spuBind({
        user_id,
        sys_code,
        spu_id
      }).then(res => {
        const nextSpu = {
          id: row.id,
          spu: row.spu || '',
          name: row.name || '',
          image: row.image || ''
        }
        this.syncBoundSpu(nextSpu)
        this.$message.success(res.msg || this.$t('message.productV2Bind'))
        this.loadList()
      }).finally(() => {
        this.bindingId = null
      })
    },
    // 解绑当前 SPU：同步列表行/详情页数据，刷新弹框列表，不刷新外层列表
    unbindSpu() {
      this.syncBoundSpu(null)
      this.$message.success(this.$t('message.productV2Unbind'))
      this.loadList()
    },
    /**
     * 同步已绑定 SPU 到弹框、当前 product（列表行或详情 detail）、以及父组件
     * @param {Object|null} nextSpu { id, spu, name, image } 或 null
     */
    syncBoundSpu(nextSpu) {
      this.boundSpu = nextSpu
      if (this.product) this.$set(this.product, 'spu', nextSpu)
      this.$emit('change', nextSpu)
    },
    /**
     * 打开创建/编辑弹窗
     * @param {Object} [row] 有值则编辑，无值则创建
     */
    openSaveDialog(row) {
      if (row) {
        this.saveForm = {
          id: row.id || '',
          spu: row.spu || '',
          name: row.name || '',
          image: row.image || ''
        }
      } else {
        this.saveForm = { id: '', spu: '', name: '', image: '' }
      }
      this.saveVisible = true
    },
    // OSS 上传单图，最多保留 1 张（再次上传覆盖）
    async uploadSpuImage(file) {
      this.uploading = true
      try {
        const result = await this.createClient(file)
        this.saveForm.image = result.data
      } finally {
        this.uploading = false
      }
    },
    // 保存 SPU（创建或编辑）
    submitSave() {
      const spu = String(this.saveForm.spu || '').trim()
      if (!spu) {
        return this.$message.error(this.$t('message.productV2EnterSpu'))
      }
      const user_id = this.getUserId()
      if (!user_id) {
        return this.$message.error(this.$t('message.customerID_CustomerName'))
      }
      const params = {
        user_id,
        spu,
        name: this.saveForm.name,
        image: this.saveForm.image
      }
      // 有 id 则编辑
      if (this.saveForm.id) params.id = this.saveForm.id
      this.saving = true
      this.$api.productManagementApi.spuSave(params).then(res => {
        this.$message.success(res.msg || this.$t('message.saveSuccess'))
        this.saveVisible = false
        this.loadList()
      }).finally(() => {
        this.saving = false
      })
    },
    // 打开删除二次确认弹窗
    deleteSpu(row) {
      this.deleteRow = row
      this.deleteVisible = true
    },
    // 确认删除 SPU：后端会自动解绑全部已绑定管理番号
    submitDelete() {
      const row = this.deleteRow
      const user_id = this.getUserId()
      if (!row || !row.id || !user_id) return
      this.deleting = true
      this.$api.productManagementApi.spuDelete({
        user_id,
        id: row.id
      }).then(res => {
        this.$message.success(res.msg || this.$t('message.successfullyDeleted'))
        this.deleteVisible = false
        this.deleteRow = null
        // 若删的是当前已绑定 SPU，同步清空列表行/详情，不刷新外层列表
        if (this.isBoundRow(row)) {
          this.syncBoundSpu(null)
        }
        this.loadList()
      }).finally(() => {
        this.deleting = false
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.dialog-body {
  min-height: 240px;
}

.bound-row {
  display: flex;
  align-items: center;
  gap: 16px;
  min-height: 44px;
  margin-bottom: 16px;
  padding: 8px 16px;
  border-radius: 4px;
  background: #f5f7fa;
}

.bound-label {
  flex: 0 0 auto;
  color: #606266;
  white-space: nowrap;
}

.bound-content {
  display: flex;
  align-items: center;
  flex: 1;
  gap: 28px;
  min-width: 0;
}

.bound-code {
 width: 200px;
  color: #303133;
  font-weight: 600;
}

.bound-pic {
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  border-radius: 4px;
  cursor: zoom-in;
}

.bound-name {
  flex: 1;
  min-width: 0;
  padding: 0 8px;
  color: #333;
  line-height: 20px;
  word-break: break-all;
}

.bound-unbind {
  flex: 0 0 auto;
  margin-left: auto;
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.search-area {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.search-area .el-input {
  width: 320px;
}

.search-area .el-input ::v-deep .el-input__inner,
.search-area .el-button,
.create-btn {
  height: 36px;
  line-height: 36px;
  padding-top: 0;
  padding-bottom: 0;
}

.create-btn {
  border: 0;
  background: #15ad3c;
  color: #fff;
}

.create-btn:hover,
.create-btn:focus {
  background: #129634;
  color: #fff;
}

.spu-table {
  overflow: hidden;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
}

.spu-table-body {
  max-height: 600px;
  overflow-x: hidden;
  overflow-y: auto;
}

.spu-grid {
  display: grid;
  grid-template-columns: minmax(200px, 1.6fr) 160px minmax(160px, 1.2fr) 200px;
}

.spu-grid > div {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  padding: 12px;
  text-align: center;
}

.table-head {
  min-height: 46px;
  background: #edeff4;
  font-weight: 700;
}

.table-row {
  min-height: 88px;
  border-top: 1px solid #dcdfe6;
  background: #fff;
}

.row-pic {
  width: 72px;
  height: 72px;
  border-radius: 4px;
  cursor: zoom-in;
}

.row-name {
  justify-content: center;
  text-align: center;
  line-height: 20px;
  word-break: break-all;
}

.row-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.action-icon {
  font-size: 18px;
  cursor: pointer;
}

.action-icon.delete {
  color: #b4272d;
}

.action-icon.edit {
  color: #409eff;
}

.empty {
  padding: 40px;
  color: #909399;
  text-align: center;
}

.pagination {
  margin-top: 16px;
  text-align: right;
}

.save-form {
  padding: 8px 20px 0;
}

.image-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.upload-btn {
  border: 0;
  background: #15ad3c;
  color: #fff;
}

.upload-btn:hover,
.upload-btn:focus {
  background: #129634;
  color: #fff;
}

.image-preview {
  position: relative;
  width: 64px;
  height: 64px;
}

.image-preview .el-image {
  width: 64px;
  height: 64px;
  border-radius: 4px;
}

.image-preview .el-icon-close {
  position: absolute;
  top: -6px;
  right: -6px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #909399;
  color: #fff;
  font-size: 12px;
  line-height: 18px;
  text-align: center;
  cursor: pointer;
}

.save-footer {
  display: flex;
  justify-content: center;
  padding: 8px 0 12px;
}

.save-btn {
  min-width: 120px;
  background: #b4272d;
  border-color: #b4272d;
}
</style>

<style lang="scss">
.bind-spu-dialog .el-dialog__body {
  padding: 12px 20px 20px;
}

.spu-save-dialog .el-dialog__body {
  padding: 12px 20px 8px;
}

.spu-delete-dialog .el-dialog__body {
  padding: 24px 28px 8px;
}

.spu-delete-dialog .delete-warning {
  margin: 0;
  color: #f5222d;
  font-weight: 700;
  line-height: 22px;
}
</style>
